import { Num, T, Var } from 'gt-react';
import { CaretRightIcon } from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { RulesSummary } from './evaluateUploadRules';
import { useRuleText } from './useRuleText';

/** One upload line that fails a rule. */
interface IssueLine {
  /** The line's position in the upload CSV, 1 to N. */
  line: number;
  /** Excel row number in the supplier's workbook. */
  sheetRow: number;
  value: string;
}

interface Props {
  summary: RulesSummary;
}

/**
 * Every line with a problem, grouped by what is wrong: one group per failing
 * rule, open by default so the lines show without a
 * click. Each line names its upload line number and its Excel row.
 */
export function IssuesSummary({ summary }: Props) {
  const { columnLabel, describeCheck, severityNames } = useRuleText();

  if (summary.results.length === 0) {
    return (
      <p className="text-sm">
        <T context="upload issues summary when every line passes">
          Every line passes the validation rules.
        </T>
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {summary.results.map(({ rule, failures }) => (
        <IssueGroup
          key={rule.id}
          badge={
            <Badge
              variant={rule.severity === 'block' ? 'destructive' : 'secondary'}
            >
              {severityNames[rule.severity]}
            </Badge>
          }
          title={`${columnLabel(rule.column)} ${describeCheck(rule.check)}`}
          lines={failures}
        />
      ))}
    </div>
  );
}

function IssueGroup({
  badge,
  title,
  lines,
}: {
  badge: React.ReactNode;
  title: React.ReactNode;
  lines: IssueLine[];
}) {
  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left text-sm">
        <CaretRightIcon className="h-3 w-3 transition-transform group-data-[panel-open]:rotate-90" />
        {badge}
        <span>{title}</span>
        <span className="text-muted-foreground">
          <T context="number of upload lines with one problem">
            <Num>{lines.length}</Num> lines
          </T>
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="max-h-48 overflow-y-auto py-1 pl-7 font-mono text-xs">
          {lines.map((issue) => (
            <li key={issue.sheetRow}>
              <IssueLineText issue={issue} />
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function IssueLineText({ issue }: { issue: IssueLine }) {
  return (
    <T context="one upload line with a problem: its line number, Excel row and value">
      Line <Num>{issue.line}</Num> · Excel row <Var>{issue.sheetRow}</Var>:{' '}
      <Var>{issue.value === '' ? '(blank)' : issue.value}</Var>
    </T>
  );
}
